import { buildGeofenceRows } from './geofenceAggregate'
import { temperatureColor } from './temperatureGradient'

import { buildAlarmsSection, buildAlertsSection, buildReeferSection } from '../historyGraph/transform'
import type {
  AssetHistory,
  LaneInterval,
  LaneRowData,
  LaneSectionData,
  LocationSample,
  NumericSample,
  SpeedSample,
  VirtualSetpointPeriod,
  ZoneHistory,
  ZoomRange,
} from '../historyGraph/types'

type SetpointLookup =
  | { kind: 'none' }
  | { kind: 'vsp'; periods: { start: number; end: number; value: number }[] }
  | { kind: 'sp'; samples: { time: number; value: number }[] }

/**
 * Build the ordered list of timeline sections for the Asset Timeline insight.
 *
 * Row order (top to bottom):
 *   Zone sections (VSP/SP, monitoring points, return air, supply air),
 *   Ambient, Door, Movement, Geofence, Reefer, Alerts, Alarms
 */
export function buildTimelineSections(data: AssetHistory, periodWindow?: ZoomRange): LaneSectionData[] {
  const sections: LaneSectionData[] = []

  // --- Zone sections (each gets its own header) ---
  const zoneSections = buildZoneSections(data)
  sections.push(...zoneSections)

  // --- Misc section (blank header, no ×): ambient, door, movement ---
  const miscRows: LaneRowData[] = []
  const ambientRow = buildAmbientRow(data.ambient)
  if (ambientRow) miscRows.push(ambientRow)
  const doorRow = buildDoorRow(data.doorEvents)
  if (doorRow) miscRows.push(doorRow)
  const movementRow = buildMovementRow(data.speed, data.location)
  if (movementRow) miscRows.push(movementRow)
  if (miscRows.length) {
    sections.push({ title: '', rows: miscRows })
  }

  // --- Reefer section ---
  const reeferRows = buildReeferRows(data)
  if (reeferRows.length) {
    sections.push({ title: 'Reefer', rows: reeferRows })
  }

  // --- Geofence ---
  const { aggregateRow, perGeofenceRows } = buildGeofenceRows(data.location)
  const geofenceRows: LaneRowData[] = []
  if (aggregateRow) geofenceRows.push(aggregateRow)
  geofenceRows.push(...perGeofenceRows)
  if (geofenceRows.length) {
    sections.push({ title: 'Geofence', rows: geofenceRows })
  }

  // --- Alerts & Alarms (filtered to period window, sorted by first occurrence) ---
  const alerts = buildAlertsSection(data.alerts)
  const filteredAlerts = filterSectionToPeriod(alerts, periodWindow)
  addLabelToTooltips(filteredAlerts)
  if (filteredAlerts.rows.length) sections.push(filteredAlerts)

  const alarms = buildAlarmsSection(data.alarms)
  const filteredAlarms = filterSectionToPeriod(alarms, periodWindow)
  addLabelToTooltips(filteredAlarms)
  if (filteredAlarms.rows.length) sections.push(filteredAlarms)

  // Prepend a section header row to each section
  for (const section of sections) {
    const isBlankHeader = section.title === ''
    section.rows.unshift({
      key: `__section_${section.title || 'misc'}__`,
      label: isBlankHeader ? '\u200B' : section.title,
      colorMapKey: isBlankHeader ? 'sectionHeaderBlank' : 'sectionHeader',
      intervals: [{ start: 0, end: Number.MAX_SAFE_INTEGER, value: 'sectionHeader' }],
    })
  }

  return sections
}

/** Build per-zone sections with VSP/SP, monitoring points, return air, supply air */
function buildZoneSections(data: AssetHistory): LaneSectionData[] {
  const sections: LaneSectionData[] = []
  const zones = data.zones ?? {}
  const zoneNames = Object.keys(zones)
  const fallbackEndMs = new Date(data.to as unknown as string).getTime()

  for (const zoneName of zoneNames) {
    const zone = zones[zoneName]
    if (!zone) continue

    const rows: LaneRowData[] = []
    const setpointLookup = buildSetpointLookup(zone, fallbackEndMs)

    // VSP or SP row — VSP renders periods directly; SP uses run-length encoding
    if (zone.virtualSetPoint?.length) {
      rows.push(buildVspRow(`zone_${zoneName}_vsp`, 'Virtual Setpoint', zone.virtualSetPoint, fallbackEndMs))
    } else if (zone.setPoint?.length) {
      rows.push(buildSetpointRow(`zone_${zoneName}_sp`, 'Setpoint', zone.setPoint))
    }

    // Monitoring points for this zone
    const monitoringPoints = data.monitoringPoints ?? {}
    for (const [mpName, samples] of Object.entries(monitoringPoints)) {
      if (!samples?.length) continue
      // Filter to monitoring points belonging to this zone
      if (samples[0]?.zoneName !== zoneName) continue

      const intervals: LaneInterval[] = []
      for (let i = 0; i < samples.length; i++) {
        const start = new Date(samples[i].time).getTime()
        const end = i < samples.length - 1 ? new Date(samples[i + 1].time).getTime() : start + 60000
        const temp = samples[i].temperature
        const sp = findSetpointAt(setpointLookup, start)
        const color = sp != null ? temperatureColor(temp, sp) : '#22c55e'
        intervals.push({
          start,
          end,
          value: color, // color stored in value for tempGradient colorMapKey
          numericValue: temp,
          tooltip: `${mpName}: ${temp.toFixed(1)}\u00b0C${sp != null ? ` (SP: ${sp.toFixed(1)}\u00b0C)` : ''}`,
        })
      }
      rows.push({
        key: `temp_${zoneName}_${mpName}`,
        label: mpName,
        colorMapKey: 'tempGradient',
        intervals,
      })
    }

    // Return Air — gradient colored around average value
    if (zone.returnAir?.length) {
      rows.push(buildGradientNumericRow(`zone_${zoneName}_return`, 'Return Air', zone.returnAir, setpointLookup))
    }

    // Supply Air — gradient colored around average value
    if (zone.supplyAir?.length) {
      rows.push(buildGradientNumericRow(`zone_${zoneName}_supply`, 'Supply Air', zone.supplyAir, setpointLookup))
    }

    if (rows.length) {
      // If key is already "Zone 1" etc., use as-is; otherwise prepend "Zone "
      const title = /^zone\s/i.test(zoneName) ? zoneName : `Zone ${zoneName}`
      sections.push({ title, rows })
    }
  }

  // Unzoned monitoring points (zoneName is null)
  const unzonedRows: LaneRowData[] = []
  const monitoringPoints = data.monitoringPoints ?? {}
  for (const [mpName, samples] of Object.entries(monitoringPoints)) {
    if (!samples?.length) continue
    if (samples[0]?.zoneName != null) continue // already handled in zone section

    const intervals: LaneInterval[] = []
    for (let i = 0; i < samples.length; i++) {
      const start = new Date(samples[i].time).getTime()
      const end = i < samples.length - 1 ? new Date(samples[i + 1].time).getTime() : start + 60000
      const temp = samples[i].temperature
      intervals.push({
        start,
        end,
        value: '#22c55e', // no setpoint reference, default green
        numericValue: temp,
        tooltip: `${mpName}: ${temp.toFixed(1)}\u00b0C`,
      })
    }
    unzonedRows.push({
      key: `temp_${mpName}`,
      label: mpName,
      colorMapKey: 'tempGradient',
      intervals,
    })
  }
  if (unzonedRows.length) {
    sections.push({ title: 'Temperature', rows: unzonedRows })
  }

  return sections
}

/** Build a setpoint/VSP row — merge consecutive samples with same value into continuous bars */
function buildSetpointRow(key: string, label: string, samples: NumericSample[]): LaneRowData {
  const intervals: LaneInterval[] = []
  let i = 0
  while (i < samples.length) {
    const startMs = new Date(samples[i].time).getTime()
    const val = samples[i].value
    // Scan forward while value is the same
    let j = i + 1
    while (j < samples.length && samples[j].value === val) j++
    // End is next different-value sample's time, or extend 24h past last sample
    const endMs = j < samples.length ? new Date(samples[j].time).getTime() : startMs + 24 * 60 * 60 * 1000
    intervals.push({
      start: startMs,
      end: endMs,
      value: 'setpoint',
      numericValue: val,
      tooltip: `${label}: ${val.toFixed(1)}\u00b0C`,
    })
    i = j
  }
  return { key, label, colorMapKey: 'setpoint', intervals }
}

/** Build a virtual-setpoint row directly from periods. Each period is one bar; null endTime
 *  extends to the chart's right edge. Gaps between periods render as empty (VSP not active). */
function buildVspRow(key: string, label: string, periods: VirtualSetpointPeriod[], fallbackEndMs: number): LaneRowData {
  const intervals: LaneInterval[] = periods.map((p) => ({
    start: new Date(p.startTime).getTime(),
    end: p.endTime ? new Date(p.endTime).getTime() : fallbackEndMs,
    value: 'setpoint',
    numericValue: p.value,
    tooltip: `${label}: ${p.value.toFixed(1)}°C`,
  }))
  return { key, label, colorMapKey: 'setpoint', intervals }
}

/** Build a gradient-colored numeric row (return air, supply air).
 *  Uses setpoint as center if available, otherwise uses the average of the samples. */
function buildGradientNumericRow(
  key: string,
  label: string,
  samples: NumericSample[],
  setpointLookup: SetpointLookup
): LaneRowData {
  // Compute average as fallback center if no setpoint
  const avg = samples.reduce((sum, s) => sum + s.value, 0) / samples.length

  const intervals: LaneInterval[] = []
  for (let i = 0; i < samples.length; i++) {
    const start = new Date(samples[i].time).getTime()
    const end = i < samples.length - 1 ? new Date(samples[i + 1].time).getTime() : start + 60000
    const temp = samples[i].value
    const sp = findSetpointAt(setpointLookup, start)
    const center = sp ?? avg
    const color = temperatureColor(temp, center)
    intervals.push({
      start,
      end,
      value: color,
      numericValue: temp,
      tooltip: `${label}: ${temp.toFixed(1)}\u00b0C${sp != null ? ` (SP: ${sp.toFixed(1)}\u00b0C)` : ''}`,
    })
  }
  return { key, label, colorMapKey: 'tempGradient', intervals }
}

/** Build ambient row — gradient colored around its average */
function buildAmbientRow(ambient: NumericSample[]): LaneRowData | null {
  if (!ambient?.length) return null
  return buildGradientNumericRow('ambient', 'Ambient', ambient, { kind: 'none' })
}

/** Build door row — black for open, blank for closed */
function buildDoorRow(doorEvents: { time: string; status: 'open' | 'closed' }[]): LaneRowData | null {
  if (!doorEvents?.length) return null

  const intervals: LaneInterval[] = []
  for (let i = 0; i < doorEvents.length; i++) {
    if (doorEvents[i].status !== 'open') continue
    const start = new Date(doorEvents[i].time).getTime()
    // Find end: next event or extend to now
    const end = i < doorEvents.length - 1 ? new Date(doorEvents[i + 1].time).getTime() : Date.now()
    intervals.push({
      start,
      end,
      value: 'open',
      tooltip:
        i === doorEvents.length - 1
          ? `Open Since ${new Date(start).toLocaleString()}`
          : `Open ${new Date(start).toLocaleTimeString()} – ${new Date(end).toLocaleTimeString()}`,
    })
  }
  if (!intervals.length) return null
  return { key: 'door', label: 'Door State', colorMapKey: 'door', intervals }
}

/** Build movement row with speed gradient and address tooltip */
function buildMovementRow(speed: SpeedSample[], locations: LocationSample[]): LaneRowData | null {
  if (!speed?.length) return null

  const intervals: LaneInterval[] = []
  for (let i = 0; i < speed.length; i++) {
    const start = new Date(speed[i].time).getTime()
    const end = i < speed.length - 1 ? new Date(speed[i + 1].time).getTime() : start + 60000
    const kmh = speed[i].speedKmsHr
    const address = findNearestAddress(locations, start)
    const tooltipParts = [`Speed: ${kmh.toFixed(1)} km/h`]
    if (address) tooltipParts.push(address)
    intervals.push({
      start,
      end,
      value: 'speed',
      numericValue: kmh,
      tooltip: tooltipParts.join('<br/>'),
    })
  }
  return { key: 'movement_speed', label: 'Movement', colorMapKey: 'speedGradient', intervals }
}

/** Find the nearest address from location samples */
function findNearestAddress(locations: LocationSample[], timeMs: number): string | null {
  if (!locations?.length) return null
  let closest: LocationSample | null = null
  let closestDiff = Infinity
  for (const loc of locations) {
    const diff = Math.abs(new Date(loc.time).getTime() - timeMs)
    if (diff < closestDiff) {
      closestDiff = diff
      closest = loc
    }
  }
  return closest?.address ?? null
}

/** Build reefer rows cherry-picked with specific color semantics */
function buildReeferRows(data: AssetHistory): LaneRowData[] {
  const reefer = buildReeferSection(data)
  const reeferMap = new Map(reefer.rows.map((r) => [r.key, r]))
  const rows: LaneRowData[] = []

  // Power State: blank for off, green for on
  const powerState = reeferMap.get('powerState')
  if (powerState) {
    const onOnly = powerState.intervals.filter((iv) => iv.value === 'O')
    if (onOnly.length) {
      rows.push({ ...powerState, intervals: onOnly })
    }
  }

  // Power Source, Operation, Engine Speed, Air Control — keep all intervals (enum rows)
  for (const key of ['powerSource', 'operation', 'engineSpeed', 'airControl'] as const) {
    const row = reeferMap.get(key)
    if (row?.intervals.length) {
      rows.push(row)
    }
  }

  return rows
}

/** Build a setpoint lookup for a zone, preferring VSP (periods) over SP (samples). */
function buildSetpointLookup(zone: ZoneHistory, fallbackEndMs: number): SetpointLookup {
  if (zone.virtualSetPoint?.length) {
    return {
      kind: 'vsp',
      periods: zone.virtualSetPoint.map((p) => ({
        start: new Date(p.startTime).getTime(),
        end: p.endTime ? new Date(p.endTime).getTime() : fallbackEndMs,
        value: p.value,
      })),
    }
  }
  if (zone.setPoint?.length) {
    return { kind: 'sp', samples: zone.setPoint.map((s) => ({ time: new Date(s.time).getTime(), value: s.value })) }
  }
  return { kind: 'none' }
}

/** Find the effective setpoint at a given time. Returns null when no setpoint is active
 *  (e.g. the time falls in a gap between VSP periods, or before the first SP sample). */
function findSetpointAt(lookup: SetpointLookup, timeMs: number): number | null {
  if (lookup.kind === 'vsp') {
    for (const p of lookup.periods) {
      if (timeMs >= p.start && timeMs < p.end) return p.value
    }
    return null
  }
  if (lookup.kind === 'sp') {
    let result: number | null = null
    for (const s of lookup.samples) {
      if (s.time <= timeMs) result = s.value
      else break
    }
    return result
  }
  return null
}

/** Filter a section to only include rows whose intervals overlap the period window,
 *  then sort by first occurrence within the window */
function filterSectionToPeriod(section: LaneSectionData, periodWindow?: ZoomRange): LaneSectionData {
  if (!periodWindow) return section
  const [pStart, pEnd] = periodWindow
  const filteredRows = section.rows.filter((row) =>
    row.intervals.some((iv) => iv.start < pEnd && iv.end > pStart && iv.start < pEnd)
  )
  // Sort by earliest interval start within the window
  const earliestInWindow = (row: LaneRowData) => {
    let earliest = Infinity
    for (const iv of row.intervals) {
      if (iv.start < pEnd && iv.end > pStart) {
        earliest = Math.min(earliest, Math.max(iv.start, pStart))
      }
    }
    return earliest
  }
  filteredRows.sort((a, b) => earliestInWindow(a) - earliestInWindow(b))
  return { ...section, rows: filteredRows }
}

/** Prepend row label to each interval's tooltip for alert/alarm sections */
function addLabelToTooltips(section: LaneSectionData): void {
  for (const row of section.rows) {
    for (const iv of row.intervals) {
      if (iv.tooltip) {
        iv.tooltip = `<b>${row.label}</b><br/>${iv.tooltip}`
      }
    }
  }
}
